import { getLocalDateKey } from './dateUtils';
import { getFlexibleEndDateFromInterval, getFlexibleIntervalForTask } from './flexibleDateUtils';
import {
    getFlexiblePrecision, getTaskEnd, getTaskStartDate, isHourlySchedule, normalizeFlexibleDate,
    TIMELINE_DEADLINE_DURATION_MINUTES,
} from './scheduleUtils';
import { Task } from '../../types';
import { PlannerTaskInfo } from './types';

export const getTaskDateUpdates = (task: Task, date: Date): Partial<Task> => {
    const previousStart = getTaskStartDate(task);
    if (task.schedule?.kind === 'deadline' && task.schedule.precision === 'hour') {
        const endDate = new Date(date.getTime() + TIMELINE_DEADLINE_DURATION_MINUTES * 60_000);
        return {
            dueDate: endDate.toISOString(),
            schedule: { ...task.schedule, endDate: endDate.toISOString(), startDate: undefined },
        };
    }
    if (task.schedule?.precision === 'hour' && task.schedule.endDate && previousStart) {
        const previousEnd = new Date(task.schedule.endDate);
        const nextEnd = new Date(previousEnd.getTime() + date.getTime() - previousStart.getTime());
        return { dueDate: date.toISOString(), schedule: { ...task.schedule, startDate: date.toISOString(), endDate: nextEnd.toISOString() } };
    }
    const precision = getFlexiblePrecision(task);
    if (!precision) return { dueDate: date.toISOString(), schedule: task.schedule ? { ...task.schedule, startDate: date.toISOString() } : undefined };

    const start = normalizeFlexibleDate(date, precision);
    const interval = getFlexibleIntervalForTask(task);
    const end = getFlexibleEndDateFromInterval(start, precision, interval);
    return {
        dueDate: start.toISOString(),
        schedule: { precision, startDate: start.toISOString(), endDate: end.toISOString() },
    };
};

export const getAllDayTaskDateUpdates = (date: Date): Partial<Task> => {
    const allDayDate = new Date(date);
    allDayDate.setHours(0, 0, 0, 0);
    return {
        dueDate: allDayDate.toISOString(),
        duration: undefined,
        schedule: {
            precision: 'day',
            kind: 'deadline',
            endDate: allDayDate.toISOString(),
        },
    };
};

const getNextAvailableDate = (taskInfo: PlannerTaskInfo, tasks: PlannerTaskInfo[], original: Date) => {
    const duration = Math.max(15, Math.round((getTaskEnd(taskInfo.task).getTime() - original.getTime()) / 60_000));
    const preferredStart = original.getHours() * 60 + original.getMinutes();
    for (let offset = 1; offset <= 365; offset += 1) {
        const candidate = new Date();
        candidate.setHours(0, 0, 0, 0);
        candidate.setDate(candidate.getDate() + offset);
        const intervals = tasks.reduce<{ start: number; end: number }[]>((result, { task }) => {
            const date = getTaskStartDate(task);
            if (task.id === taskInfo.task.id || !date || (task.schedule && !isHourlySchedule(task))) return result;
            if (getLocalDateKey(date) !== getLocalDateKey(candidate)) return result;
            const start = date.getHours() * 60 + date.getMinutes();
            const taskEnd = getTaskEnd(task);
            const end = taskEnd.getDate() === date.getDate() ? taskEnd.getHours() * 60 + taskEnd.getMinutes() : 1440;
            result.push({ start, end: Math.min(1440, Math.max(start + 15, end)) });
            return result;
        }, []).sort((a, b) => a.start - b.start || a.end - b.end);
        let start = preferredStart;
        for (const interval of intervals) {
            if (interval.end <= start) continue;
            if (interval.start >= start + duration) break;
            start = Math.max(start, interval.end);
        }
        if (start + duration <= 1440) {
            candidate.setHours(Math.floor(start / 60), start % 60, 0, 0);
            return candidate;
        }
    }
    return null;
};

export const getRescheduledDate = (
    taskInfo: PlannerTaskInfo, option: string, tasks: PlannerTaskInfo[], customTime?: string
) => {
    const original = getTaskStartDate(taskInfo.task);
    if (!original) return null;
    const next = new Date();
    next.setSeconds(0, 0);
    next.setHours(original.getHours(), original.getMinutes(), 0, 0);
    const todayTimes: Record<string, number> = {
        'today-morning': 9, 'today-afternoon': 15, 'today-evening': 19, 'today-night': 22,
    };
    if (option in todayTimes) next.setHours(todayTimes[option], 0, 0, 0);
    else if (option === 'today-custom' && customTime) {
        const [hours, minutes] = customTime.split(':').map(Number);
        next.setHours(hours, minutes, 0, 0);
    } else if (option === 'later-tomorrow') next.setDate(next.getDate() + 1);
    else if (option === 'later-two-days') next.setDate(next.getDate() + 2);
    else if (option === 'later-next-week') next.setDate(next.getDate() + (((8 - next.getDay()) % 7) || 7));
    else if (option === 'later-next-available') return getNextAvailableDate(taskInfo, tasks, original);
    else return null;
    return next;
};
